import { useCallback } from 'react';
import { Box } from '@mui/material';
import noop from 'lodash/noop';
import { color } from '@/colors';
import { FileViewer } from '@/components/Nodes/Shared/FileViewer';
import { UploadedAsset } from '@/types/api/assets';
import { useIsHovered } from '@/hooks/useIsHovered';
import { GalleryFileViewer } from '../Common/ImageList/GalleryFileViewer';
import { ThreeDProps } from '../Common/ImageList/types';
import type { RefObject, MouseEvent } from 'react';
import type { FileKind, NodeId } from 'web';

export interface GridItemProps {
  nodeId: NodeId;
  file: FileKind | UploadedAsset;
  index: number;
  isSelected: boolean;
  onFileClick: (index: number) => void;
  shouldUseLegacyFileViewer: boolean;
  threeDProps?: ThreeDProps;
  maxSteps?: number;
  handleImageLoad?: () => void;
  onContextMenu?: (event: MouseEvent, index: number) => void;
  onTextChange?: (index: number, newValue: string) => void;
  selected: number;
  // eslint-disable-next-line react19-ref/require-ref-object-null
  videoRef?: RefObject<HTMLVideoElement>;
  fixedItemSize?: number;
}

export const GridItem = ({
  nodeId,
  file,
  index,
  isSelected,
  onFileClick,
  shouldUseLegacyFileViewer,
  threeDProps,
  maxSteps,
  handleImageLoad = noop,
  onContextMenu,
  onTextChange,
  selected,
  videoRef,
  fixedItemSize,
}: GridItemProps) => {
  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();
  const handleFileClick = useCallback(() => {
    onFileClick(index);
  }, [onFileClick, index]);

  return (
    <Box
      onClick={handleFileClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        borderRadius: 2.5,
        border: isSelected ? `2px solid ${color.Yellow40}` : `none`,
        transition: 'all 0.1s ease-in-out',
        overflow: 'hidden',
        height: fixedItemSize ? `${fixedItemSize}px` : 'auto',
        position: 'relative',

        '&::before': {
          // this is used to create a hover effect
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: isSelected ? 'transparent' : 'rgba(14, 14, 19, 0.5)',
          transition: 'background-color 0.1s ease-in-out',
          pointerEvents: 'none',
          zIndex: 1,
        },
        '&:hover::before': {
          backgroundColor: isSelected ? 'transparent' : 'rgba(14, 14, 19, 0.3)',
        },
      }}
      className="nodrag"
    >
      {shouldUseLegacyFileViewer ? (
        <GalleryFileViewer
          cameraPosition={threeDProps?.cameraPosition} // TODO every file should get its own camera position
          container="node"
          index={index}
          file={file as UploadedAsset}
          handleImageLoad={handleImageLoad}
          is3DLocked={threeDProps?.is3DLocked}
          isHovered={isHovered}
          maxSteps={maxSteps}
          onContextMenu={onContextMenu}
          onTextChange={onTextChange}
          overlay={null}
          resetTransformRef={undefined} // this is used when in app or in app gallery mode, not relevant in node view
          selected={selected}
          setCameraPosition={noop}
          setScale={noop} // this is used when in app or in app gallery mode, not relevant in node view
          transformKey={0} // this is used when in app or in app gallery mode, not relevant in node view
          videoRef={videoRef}
          isMultiFilesViewMode={true}
          fixedItemSize={fixedItemSize}
        />
      ) : (
        <FileViewer
          isMultiFilesViewMode={true}
          id={nodeId}
          index={index}
          asset={file}
          isClickable={true}
          fixedItemSize={fixedItemSize}
        />
      )}
    </Box>
  );
};
